import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Chip,
  keyframes,
} from '@mui/material';
import { Check, Warning } from '@mui/icons-material';
import { useFormData } from '../../../contexts/FormDataContext';
import { useConfidenceToggle } from '../../../contexts/ConfidenceToggleContext';

// Match Tab2 section/form border (#8DBAEA)
const SYMPTOM_BORDER_COLOR = '#8DBAEA';

const glowPulse = keyframes`
  0% { box-shadow: 0 0 4px 1px rgba(0, 150, 136, 0.4); }
  50% { box-shadow: 0 0 10px 3px rgba(0, 150, 136, 0.6); }
  100% { box-shadow: 0 0 4px 1px rgba(0, 150, 136, 0.4); }
`;

// 18 symptoms – order matches design: two columns, row by row (left | right)
const SYMPTOMS = [
  { key: 'symptom_unexplained_weight_loss', label: 'Unexplained weight loss' },
  { key: 'symptom_abdominal_pain', label: 'Abdominal pain' },
  { key: 'symptom_blurred_vision', label: 'Blurred vision / vision changes' },
  { key: 'symptom_increased_thirst', label: 'Increased thirst' },
  { key: 'symptom_irritable_mood', label: 'Feeling irritable or other mood changes' },
  { key: 'symptom_hyperglycemia', label: 'Hyperglycemia' },
  { key: 'symptom_headache', label: 'Headache' },
  { key: 'symptom_fatigue', label: 'Fatigue' },
  { key: 'symptom_recurrent_infections', label: 'Recurrent infections' },
  { key: 'symptom_polyuria', label: 'Polyuria / frequent urination' },
  { key: 'symptom_new_bed_wetting', label: 'New bed wetting' },
  { key: 'symptom_very_hungry', label: 'Feeling very hungry' },
  { key: 'symptom_poor_wound_healing', label: 'Poor wound healing' },
  { key: 'symptom_fruity_breath', label: 'Fruity Smelling Breath' },
  { key: 'symptom_nausea_vomiting', label: 'Nausea or vomiting' },
  { key: 'symptom_genital_itching', label: 'Genital itching' },
  { key: 'symptom_yeast_infection_prepuberty', label: 'Yeast infection pre-puberty (females only)' },
  { key: 'symptom_menstrual_changes', label: 'Menstrual changes (females only)' },
];

interface SymptomCheckboxGroupProps {
  tabName: string;
}

export default function SymptomCheckboxGroup({ tabName }: SymptomCheckboxGroupProps) {
  const { getFieldValue, getFieldConfidence, getFieldSource, updateField } = useFormData();
  const { showConfidence } = useConfidenceToggle();

  // Glow effect for AI-updated symptoms (same as FormField / lab report values)
  const [glowingKeys, setGlowingKeys] = useState<Set<string>>(new Set());
  const prevValuesRef = useRef<Record<string, boolean>>({});
  const glowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Snapshot so effect runs when any symptom value or source changes (e.g. WebSocket update)
  const valueSnapshot = SYMPTOMS.map(
    (s) => `${s.key}:${getFieldValue(tabName, s.key, false)}:${getFieldSource(tabName, s.key)}`
  ).join('|');

  useEffect(() => {
    const nextGlowing = new Set<string>();
    SYMPTOMS.forEach(({ key }) => {
      const value = getFieldValue(tabName, key, false);
      const source = getFieldSource(tabName, key);
      const prev = prevValuesRef.current[key];
      const checked = Boolean(value);
      if (
        prev !== checked &&
        source &&
        source !== 'manual' &&
        source !== 'database'
      ) {
        nextGlowing.add(key);
      }
      prevValuesRef.current[key] = checked;
    });
    if (nextGlowing.size > 0) {
      setGlowingKeys((prev) => new Set([...prev, ...nextGlowing]));
      if (glowTimerRef.current) clearTimeout(glowTimerRef.current);
      glowTimerRef.current = setTimeout(() => setGlowingKeys(new Set()), 8000);
    }
    return () => {
      if (glowTimerRef.current) clearTimeout(glowTimerRef.current);
    };
  }, [tabName, valueSnapshot]);

  const handleToggle = (fieldName: string) => () => {
    const current = Boolean(getFieldValue(tabName, fieldName, false));
    updateField(tabName, fieldName, !current);
  };

  const getConfidenceBadge = (fieldName: string) => {
    const confidence = getFieldConfidence(tabName, fieldName);
    const source = getFieldSource(tabName, fieldName);

    if (!source || source === 'manual' || source === 'database') {
      return null;
    }

    const getColor = () => {
      if (confidence >= 0.9) return '#009688'; // Teal
      if (confidence >= 0.7) return '#ff9800'; // Orange
      return '#e91e63'; // Pink
    };

    const getIcon = () => {
      if (source === 'ambient') return <Check sx={{ fontSize: 12 }} />;
      if (source === 'llm_correction') return <Warning sx={{ fontSize: 12 }} />;
      return null;
    };

    return (
      <Tooltip title={`${(confidence * 100).toFixed(0)}% confidence (${source})`}>
        <Chip
          icon={getIcon() || undefined}
          label={`${(confidence * 100).toFixed(0)}%`}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.65rem',
            bgcolor: getColor(),
            color: 'white',
            ml: 0.5,
            '& .MuiChip-icon': { color: 'white' },
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1,
      }}
    >
      {SYMPTOMS.map(({ key, label }) => {
        const selected = Boolean(getFieldValue(tabName, key, false));
        const source = getFieldSource(tabName, key);
        const isAutoFilled = source === 'ambient' || source === 'llm_correction';

        return (
          <Box
            key={key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={handleToggle(key)}
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                px: 1.5,
                py: 1,
                border: '1px solid',
                borderColor: glowingKeys.has(key) ? 'rgba(0, 150, 136, 0.6)' : SYMPTOM_BORDER_COLOR,
                borderRadius: 1,
                bgcolor: selected ? 'rgba(25, 118, 210, 0.08)' : '#ffffff',
                color: selected ? 'primary.main' : '#424242',
                fontSize: '0.8rem',
                fontWeight: selected ? 600 : 400,
                cursor: 'pointer',
                textAlign: 'left',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                ...(isAutoFilled && { bgcolor: selected ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.03)' }),
                ...(glowingKeys.has(key) && {
                  animation: `${glowPulse} 1.5s ease-in-out infinite`,
                  boxShadow: '0 0 6px 2px rgba(0, 150, 136, 0.4)',
                }),
                '&:hover': {
                  borderColor: selected ? 'primary.main' : '#bdbdbd',
                  bgcolor: selected ? 'rgba(25, 118, 210, 0.14)' : '#fafafa',
                },
              }}
            >
              {label}
            </Box>
            {showConfidence && getConfidenceBadge(key)}
          </Box>
        );
      })}
    </Box>
  );
}
